import {DeepReadonly} from 'ts-essentials'
import {logger, Util} from 'yougile-util'
import {
  createDataObjectId,
  DataObjectId,
  Department,
  departmentsContainerId,
  getStorageHierarchy,
  Hub,
  SandboxTree,
  StorageDepartmentHierarchy
} from 'yougile-model'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {GanttDraft} from '@extensions/gantt-draft'
import {systemStickerCtl} from '@extensions/sticker/sticker'
import {SyncEvents} from './sync-events'

export class DepartmentsClient {
  private static cachedHierarchies: Map<
    DataObjectId,
    StorageDepartmentHierarchy
  > = new Map()
  private static initialized = false

  public static init(): void {
    if (this.initialized) {
      logger.log('DepartmentsClient is already initialized')
      return
    }
    this.initialized = true
    SyncEvents.addAnyListener(companyId => {
      this.cachedHierarchies.delete(companyId)
    })
  }

  public static createDepartment(
    box: SandboxTree,
    title: string,
    parentId?: DataObjectId
  ): Department {
    const id = createDataObjectId()
    const hub = new Hub(box.companyId, id)
    hub.id = id
    hub.data.department = {
      id,
      title,
      parentId,
      managers: [],
      members: []
    }
    box.add(hub, departmentsContainerId, false, false)
    return {...hub.data.department}
  }

  public static getHierarchy(box: SandboxTree): StorageDepartmentHierarchy {
    if (!box) {
      return undefined
    }
    const cached = this.cachedHierarchies.get(box.companyId)
    if (cached) {
      return cached
    }
    const deps = DepartmentsClient.listDepartments(box)
    const userTagId = systemStickerCtl.User.getSticker(box)?.id
    const res = getStorageHierarchy(userTagId, deps)
    this.cachedHierarchies.set(box.companyId, res)
    return res
  }

  public static listDepartments(box: SandboxTree): Department[] {
    const hubs = box.getChildrenHubs(departmentsContainerId, {})
    return Util.clone(hubs.map(h => h.data.department).filter(d => !!d))
  }

  public static listRoDepartments(
    box: SandboxTree
  ): DeepReadonly<Department[]> {
    const hubs = box.getChildrenHubs(departmentsContainerId, {})
    return hubs.map(h => h.data.department).filter(d => !!d)
  }

  public static listForUser(
    box: SandboxTree,
    userId: DataObjectId
  ): DeepReadonly<Department[]> {
    const deps = DepartmentsClient.listRoDepartments(box)
    return deps.filter(
      d => d.managers.includes(userId) || d.members.includes(userId)
    )
  }

  public static isManager(box: SandboxTree, userId: DataObjectId): boolean {
    const deps = DepartmentsClient.listRoDepartments(box)
    return deps && !!deps.find(dep => dep.managers.includes(userId))
  }

  public static getDepartment(box: SandboxTree, id: DataObjectId): Department {
    const hub = box.get(id) as Hub
    return hub?.data.department ? Util.clone(hub.data.department) : undefined
  }

  public static updateDepartment(box: SandboxTree, dep: Department): void {
    const hub = box.get(dep.id) as Hub
    hub.data.department = Util.clone(dep)
    hub.commit()
    Tracking.send(TrackingEvent.depsEdit)
  }

  public static remove(box: SandboxTree, dep: Department): void {
    const hub = box.get(dep.id) as Hub
    if (hub) {
      box.remove(hub)
      GanttDraft.planEntitySubtree(hub.id, box)
      Tracking.send(TrackingEvent.depsEdit)
    }
  }

  public static setRole(
    box: SandboxTree,
    userId: DataObjectId,
    dep: Department,
    role: 'manager' | 'member' | 'excluded'
  ): void {
    const excluded = (arr: DataObjectId[]) =>
      arr.filter((uid: DataObjectId) => uid !== userId)
    const withoutUser = {
      ...dep,
      managers: excluded(dep.managers),
      members: excluded(dep.members)
    }
    if (role === 'excluded') {
      DepartmentsClient.updateDepartment(box, withoutUser)
    } else if (role === 'manager') {
      DepartmentsClient.updateDepartment(box, {
        ...withoutUser,
        managers: [...withoutUser.managers, userId]
      })
    } else if (role === 'member') {
      DepartmentsClient.updateDepartment(box, {
        ...withoutUser,
        members: [...withoutUser.members, userId]
      })
    }
  }
}
